import React, { useState, useEffect } from 'react';
import {
  CheckCircle2, Mail, RefreshCw, Send,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WeeklyDigestCard() {
  const [settings, setSettings] = useState(null);
  const [email, setEmail] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [day, setDay] = useState('monday');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState('');

  useEffect(() => {
    const t = sessionStorage.getItem('admin_token');
    if (!t) return;
    fetch(`${API_URL}/api/admin/weekly-digest/settings`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) { setSettings(d); setEmail(d.recipient_email || ''); setEnabled(d.enabled || false); setDay(d.day || 'monday'); }
      }).catch(() => {});
  }, []);

  const handleSave = async () => {
    const t = sessionStorage.getItem('admin_token');
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/admin/weekly-digest/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
        body: JSON.stringify({ enabled, recipient_email: email, day }),
      });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error('Failed to save weekly digest settings:', e); }
    setSaving(false);
  };

  const handleSendNow = async () => {
    const t = sessionStorage.getItem('admin_token');
    setSending(true); setSendResult('');
    try {
      const r = await fetch(`${API_URL}/api/admin/weekly-digest?send_email=true`, { headers: { Authorization: `Bearer ${t}` } });
      const d = await r.json();
      setSendResult(d.email_sent ? `Sent to ${d.email_recipient || 'configured email'}` : `Failed: ${d.email_error || 'Unknown error'}`);
    } catch(e) { setSendResult('Failed to send'); }
    setSending(false);
  };

  return (
    <div className="bg-white rounded-xl border-2 border-teal-100 p-6" data-testid="weekly-digest-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Mail className="w-4 h-4 text-teal-500" /> Weekly Performance Digest
        </h3>
        <button onClick={() => setEnabled(!enabled)}
          className={`w-10 h-5 rounded-full relative transition-colors ${enabled ? 'bg-teal-500' : 'bg-gray-200'}`}
          data-testid="digest-toggle">
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${enabled ? 'left-5' : 'left-0.5'}`} />
        </button>
      </div>
      <p className="text-xs text-gray-400 mb-4">Automated weekly email with article views, affiliate clicks, revenue, peptide stack conversions, and cart recovery stats.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold block mb-1">Recipient Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-teal-400 focus:ring-1 focus:ring-teal-400 outline-none"
            data-testid="digest-email-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold block mb-1">Send Day</label>
          <select value={day} onChange={e => setDay(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-teal-400 outline-none"
            data-testid="digest-day-select">
            {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => (
              <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end gap-2">
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-teal-500 text-white text-xs font-semibold rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 flex items-center gap-1"
            data-testid="digest-save-btn">
            {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
            {saved ? <><CheckCircle2 className="w-3.5 h-3.5" /> Saved</> : saving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={handleSendNow} disabled={sending}
            className="px-4 py-2 border border-gray-200 text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-1"
            data-testid="digest-send-now-btn">
            {sending ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {sending ? 'Sending...' : 'Send Now'}
          </button>
        </div>
      </div>
      {sendResult && (
        <p className={`text-xs ${sendResult.startsWith('Sent') ? 'text-teal-600' : 'text-red-500'}`} data-testid="digest-send-result">{sendResult}</p>
      )}
      {settings?.last_sent && (
        <p className="text-[10px] text-gray-400 mt-1">Last sent: {new Date(settings.last_sent).toLocaleString()}</p>
      )}
    </div>
  );
}
